import React, { Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SearchResults } from '../../../types/Search.types';
import { Namespace } from '../../../translation/namespace';
import { Hyperlink } from '../../../components/atoms/Hyperlink';
import { StatusInfoError } from '../../../_lib/components/StatusInfo';
import { ISonURL } from '../../../types/IUrl';

type SearchFormResults = {
  searchResult: SearchResults;
  pageList?: boolean; // different texts in PageLists, see SON-17769
};

export function SearchFormResults({ searchResult, pageList }: SearchFormResults) {
  const { t, i18n } = useTranslation(Namespace.SearchFormResults);

  const searchFormResultLink = () => {
    if (searchResult.sectionFilter?.sectionLink.url) {
      return (
        <Hyperlink to={searchResult.sectionFilter?.sectionLink.url} className="search-form-results__link">
          {searchResult.sectionFilter?.sectionLink.name}
        </Hyperlink>
      );
    }
    return null;
  };

  const getTranslatedTextWithLink = () => {
    // different text in page lists, see SON-17769
    if (pageList) {
      return t('resultTextPageList', { count: searchResult.numResults });
    }

    // unfortunately multiple contexts seem not to be supported in i18next,
    // so we need an if-statement here
    const values = {
      count: searchResult.numResults,
      // i18next context feature: https://www.i18next.com/translation-function/context
      context: null,
    };
    if (searchResult.sectionFilter && searchResult.sectionFilter.sectionLink) {
      return (
        <Trans
          // @ts-expect-error ignore Trans component, should not be used
          i18nKey="resultTextSectionFilter"
          // @ts-expect-error ignore Trans component, should not be used
          i18n={i18n}
          // @ts-expect-error ignore Trans component, should not be used
          t={t}
          // not sure why this multiple passing is necessary, but it is
          {...values}
          // @ts-expect-error ignore Trans component, should not be used
          values={values}
          // @ts-expect-error ignore Trans component, should not be used
          components={[searchFormResultLink()]}
        />
      );
    }
    // no search results
    if (searchResult.numResults === 0) {
      return t('resultTextNone');
    }
    // one search result
    if (searchResult.numResults === 1) {
      return t('resultTextSingular', { count: searchResult.numResults });
    }
    // all other results
    return t('resultText', { count: searchResult.numResults });
  };

  if (searchResult && searchResult.error) {
    return <StatusInfoError message={t(searchResult.error)} />;
  }

  const suggestionsResultLink = (spellCheckSuggestions: ISonURL[]) => {
    return (
      <>
        {' '}
        <Trans i18nKey="resultTextSuggestionsTmp" i18n={i18n} t={t}>
          {` ${t('resultTextSuggestions')} `}
          <>
            {spellCheckSuggestions.map((suggestion, index) => (
              <Fragment key={suggestion.name}>
                <Hyperlink
                  key={suggestion.name}
                  to={suggestion.url}
                  className="search-form-results__link font-bold-15-21"
                >
                  {suggestion.name}
                </Hyperlink>
                {index !== spellCheckSuggestions.length - 1 ? ', ' : ''}
              </Fragment>
            ))}
          </>
        </Trans>
        ?
      </>
    );
  };

  return (
    <p className="search-form-results__count font-regular-15-21" id="search-results-text">
      {getTranslatedTextWithLink()}
      {searchResult.spellCheckSuggestions?.length > 0 && suggestionsResultLink(searchResult.spellCheckSuggestions)}
    </p>
  );
}
